// The conversation (design review ui.component_recipe core.ai_chat, container
// plain_spacing): the user's question in a layer-2 bubble at the inline end,
// the answer on the page at the start. The signature of this surface is the
// contrast between the two kinds of answer: every answer opens with a badge
// (a saved answer filled, an AI answer outlined) that names where it came from and one meta line that says what that
// means (a saved answer: checked, no AI quota; an AI answer: check it, with
// its source count), then its Rujukan in readable words. The backend's
// "Catatan:" reminder stays in the answer (the KAK guardrail) as its last
// line, a muted footnote after Rujukan. Every answer renders through the safe Markdown
// renderer: model text is never HTML.
// A document proposal (contract section 10) renders as the Usulan dokumen card
// (./Proposal.tsx) for roles that can generate; any other role reads only its text.
// Phase 2: a question shows its files as chips above the bubble (download
// links); an answer with a structured document shows the DocumentCard.
import type { AttachmentRef, ChatMessage, ChatTurnInput, KnowledgeFile } from "../api";
import { ApiError, knowledgeFileUrl } from "../api";
import { citationLabel } from "../lib/citations";
import { Markdown } from "../lib/markdown";
import { Glyph } from "../ui/icons";
import { MessageAttachments } from "./AttachmentChips";
import { DocumentCard } from "./DocumentCard";
import { PaketProposalCard } from "../project/PaketProposalCard";
import { CreatedCard, ProposalCard, type CatalogState } from "./Proposal";
import { messageKind, type CreateDocumentResult, type ThreadMessage } from "./documents";
import { TurnState } from "./TurnState";

/** A turn in flight or failed; bubble is false when the question is already stored and shown. */
export type Pending =
  | { status: "sending"; input: ChatTurnInput; text: string; bubble: boolean; files?: AttachmentRef[] }
  | { status: "error"; input: ChatTurnInput; text: string; bubble: boolean; error: ApiError; files?: AttachmentRef[] };

export { sectionLabel } from "../lib/citations";

/** Splits a trailing "Catatan:" paragraph off the answer, so it reads as a footnote. */
export function splitNote(content: string): { body: string; note: string | null } {
  const m = /(?:^|\n\s*\n)\s*(Catatan:[\s\S]*)$/.exec(content);
  if (!m || m.index === undefined) return { body: content, note: null };
  const body = content.slice(0, m.index).trimEnd();
  return body ? { body, note: m[1]!.trim() } : { body: content, note: null };
}

function Citations({ ids, files }: { ids: string[]; files: KnowledgeFile[] | null }) {
  if (ids.length === 0) return null;
  return (
    <div className="msg-cites">
      <p className="msg-cites-title">Rujukan</p>
      <ul className="citations">
        {ids.map((c) => {
          const { title, where, driveId } = citationLabel(c, files);
          const text = (
            <span className="citation-text">
              <span className="citation-title">{title}</span>
              {where ? <span className="citation-where">{where}</span> : null}
            </span>
          );
          return (
            <li key={c} className="citation">
              <Glyph name="link" size={16} />
              {/* A Drive-generated entry opens its original document in a new
                  tab; a hand-written knowledge file has none, so it stays text. */}
              {driveId ? (
                <a className="citation-link" href={knowledgeFileUrl(driveId)} target="_blank" rel="noreferrer" title={`Buka dokumen: ${title}`}>
                  {text}
                </a>
              ) : (
                text
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SourceLabel({ message }: { message: ChatMessage }) {
  const faq = message.source === "faq";
  const n = message.citations.length;
  const meta = faq ? "Sudah diperiksa, tanpa kuota" : `Periksa sebelum dipakai${n > 0 ? `, ${n} rujukan` : ""}`;
  return (
    <div className="msg-source" data-source={faq ? "faq" : "ai"}>
      <span className="badge msg-badge">
        <Glyph name={faq ? "bookOpen" : "magicStar"} size={16} />
        <span>{faq ? "Jawaban tersimpan" : "Dijawab Pluto"}</span>
      </span>
      <span className="msg-source-meta">{meta}</span>
    </div>
  );
}

function UserBubble({ text, files = [] }: { text: string; files?: AttachmentRef[] }) {
  return (
    <li className="msg" data-role="user">
      <MessageAttachments items={files} />
      <p className="msg-bubble">{text}</p>
    </li>
  );
}

interface DocumentProps {
  conversationId: string | null;
  catalog: CatalogState;
  /** Document ids a proposal card in this thread already confirms. */
  confirmed: Set<string>;
  onDocumentCreated: (messageId: string, result: CreateDocumentResult) => void;
}

function Answer({ m, knowledge, canDraft, docs }: { m: ThreadMessage; knowledge: KnowledgeFile[] | null; canDraft: boolean; docs: DocumentProps }) {
  const { body, note } = splitNote(m.content);
  const kind = canDraft ? messageKind(m) : "text";
  if (kind === "document_created" && m.documentId && !docs.confirmed.has(m.documentId)) {
    return (
      <li className="msg" data-role="assistant" data-message-id={m.id} data-kind={kind}>
        <CreatedCard documentId={m.documentId} content={body.trim() || null} />
      </li>
    );
  }
  if (kind === "document_proposal" && docs.conversationId) {
    return (
      <li className="msg" data-role="assistant" data-message-id={m.id} data-kind={kind}>
        <SourceLabel message={m} />
        {body.trim() ? <Markdown source={body} className="msg-md" headingOffset={0} /> : null}
        <ProposalCard message={m} conversationId={docs.conversationId} catalog={docs.catalog} onCreated={docs.onDocumentCreated} />
        {note ? <Markdown source={note} className="msg-note" headingOffset={0} /> : null}
      </li>
    );
  }
  const attachments = m.attachments ?? [];
  return (
    <li className="msg" data-role="assistant" data-message-id={m.id}>
      <SourceLabel message={m} />
      <Markdown source={body} className="msg-md" headingOffset={0} />
      {/* Paket intake (Penyedia paket project): the proposal card. */}
      {m.paketProposal ? <PaketProposalCard proposal={m.paketProposal} messageId={m.id} /> : null}
      {m.document ? (
        <DocumentCard document={m.document} attachments={attachments} conversationId={docs.conversationId} messageId={m.id} />
      ) : (
        <MessageAttachments items={attachments.filter((a) => a.kind === "generated")} label="File dari jawaban" />
      )}
      <Citations ids={m.citations} files={knowledge} />
      {note ? <Markdown source={note} className="msg-note" headingOffset={0} /> : null}
    </li>
  );
}

export interface ThreadProps {
  messages: ThreadMessage[];
  pending: Pending | null;
  /** The last stored message is a question the AI never answered. */
  unanswered: boolean;
  knowledge: KnowledgeFile[] | null;
  canDraft: boolean;
  onRetry: () => void;
  onAnswerAgain: () => void;
  /** The open conversation (create-document needs it); null before the first answer. */
  conversationId?: string | null;
  catalog?: CatalogState;
  onDocumentCreated?: (messageId: string, result: CreateDocumentResult) => void;
  /** A guest conversation: nothing is stored, so no failure says it was. */
  guest?: boolean;
}

const NO_CATALOG: CatalogState = { data: null, error: null, reload: () => undefined };

export function Thread({ messages, pending, unanswered, knowledge, canDraft, onRetry, onAnswerAgain, conversationId = null, catalog = NO_CATALOG, onDocumentCreated = () => undefined, guest = false }: ThreadProps) {
  const confirmed = new Set<string>();
  for (const m of messages) if (m.documentId && messageKind(m) === "document_proposal") confirmed.add(m.documentId);
  const docs: DocumentProps = { conversationId, catalog, confirmed, onDocumentCreated };
  return (
    <ol className="thread" aria-label="Percakapan">
      {messages.map((m) => (m.role === "user" ? <UserBubble key={m.id} text={m.content} files={m.attachments ?? []} /> : <Answer key={m.id} m={m} knowledge={knowledge} canDraft={canDraft} docs={docs} />))}
      {pending?.bubble ? <UserBubble text={pending.text} files={pending.files} /> : null}
      {unanswered ? (
        <li className="msg" data-role="assistant">
          <TurnState
            tone="warning"
            title="Pertanyaan ini belum terjawab"
            lines={["Pluto tidak menjawab saat pertanyaan ini dikirim. Coba lagi untuk meminta jawabannya."]}
            action={<RetryButton onClick={onAnswerAgain} />}
          />
        </li>
      ) : null}
      {pending?.status === "sending" ? (
        <li className="msg" data-role="assistant">
          <div className="msg-wait" role="status" aria-live="polite">
            <p className="msg-wait-title">{"faqId" in pending.input ? "Membuka jawaban tersimpan" : "Menyusun jawaban"}</p>
            <div className="ui-progress">
              <progress aria-label="Menyusun jawaban" />
            </div>
          </div>
        </li>
      ) : null}
      {pending?.status === "error" ? (
        <li className="msg" data-role="assistant">
          <TurnError error={pending.error} onRetry={onRetry} guest={guest} files={(pending.files?.length ?? 0) > 0} />
        </li>
      ) : null}
    </ol>
  );
}

function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="btn btn-secondary" onClick={onClick}>
      <Glyph name="refresh" />
      <span>Coba lagi</span>
    </button>
  );
}

function TurnError({ error, onRetry, guest, files }: { error: ApiError; onRetry: () => void; guest: boolean; files: boolean }) {
  // A turn with files that the API refused because of them (404 not yours,
  // 409 used elsewhere, 422 outside the project, 503 storage off).
  if (files && [404, 409, 422, 503].includes(error.status) && !error.conversationId) {
    const lines = [error.status === 503 ? "Penyimpanan file belum tersedia. Hubungi admin." : error.message, "Lampirkan file lagi atau kirim tanpa lampiran."];
    return <TurnState tone="danger" title="Lampiran belum dapat dikirim" lines={lines} />;
  }
  const limited = error.status === 429;
  const aiDown = error.status === 502 || error.status === 0 || error.status >= 500;
  // 422: the conversation is full (200 messages) or the saved question is gone.
  const full = error.status === 422;
  const title = limited ? "Batas penggunaan tercapai" : aiDown ? "Jawaban belum tersedia" : full ? "Percakapan tidak dapat dilanjutkan" : "Pertanyaan belum terkirim";
  const action = full ? (
    <a className="btn btn-secondary" href="/">
      <Glyph name="messageAdd" />
      <span>Percakapan baru</span>
    </a>
  ) : limited || aiDown ? (
    <RetryButton onClick={onRetry} />
  ) : undefined;
  const lines = [error.message];
  if (error.status === 502 && !guest) lines.push("Pertanyaan Anda tetap tersimpan di percakapan ini.");
  return <TurnState tone={limited ? "warning" : "danger"} title={title} lines={lines} action={action} />;
}
